import { cn } from '@tempo-io/sui-class-utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@tempo-io/sui-tooltip';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';

const OVERFLOW_TOLERANCE_PX = 1;
const TOOLTIP_DELAY_MS = 150;

const getIsOverflown = (el: HTMLElement): boolean => {
  return (
    el.scrollWidth - el.clientWidth > OVERFLOW_TOLERANCE_PX ||
    el.scrollHeight - el.clientHeight > OVERFLOW_TOLERANCE_PX
  );
};

export interface OverflowTooltipProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'content' | 'children'> {
  children: ReactNode;
  tooltipContent?: ReactNode;
  tooltipSide?: 'top' | 'bottom' | 'left' | 'right';
  tooltipAlign?: 'start' | 'center' | 'end';
  tooltipSideOffset?: number;
  className?: string;
  textClassName?: string;
  tooltipClassName?: string;
  as?: 'span' | 'div' | 'p';
  multiLine?: boolean;
  focusable?: boolean;
}

/**
 * OverflowTooltip - Displays a tooltip with full content when text is truncated
 *
 * @param multiLine - When true, allows multi-line text (use with line-clamp classes).
 *                    When false (default), uses single-line truncation.
 */
export const OverflowTooltip = ({
  children,
  tooltipContent,
  tooltipSide = 'bottom',
  tooltipAlign = 'start',
  tooltipSideOffset = 6,
  className,
  textClassName,
  tooltipClassName,
  as: Component = 'span',
  multiLine = false,
  focusable = false,
  ...containerProps
}: OverflowTooltipProps) => {
  const textRef = useRef<HTMLElement>(null);
  const [isOverflown, setIsOverflown] = useState<boolean>(false);

  const handlePointerEnter = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = textRef.current;
    if (el) {
      const next = getIsOverflown(el);
      if (next !== isOverflown) {
        setIsOverflown(next);
      }
    }
    containerProps.onPointerEnter?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLDivElement>) => {
    const el = textRef.current;
    if (el && getIsOverflown(el)) {
      setIsOverflown(true);
    }
    containerProps.onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    setIsOverflown(false);
    containerProps.onBlur?.(e);
  };

  if (!children) {
    return null;
  }

  const baseTextClasses = multiLine ? 'block' : 'truncate block';

  return (
    <Tooltip delayDuration={TOOLTIP_DELAY_MS}>
      <TooltipTrigger asChild>
        {/* biome-ignore lint/a11y/noStaticElementInteractions: TooltipTrigger asChild provides interactive semantics; focusable mode is opt-in for keyboard tooltip activation */}
        <div
          {...containerProps}
          className={cn('overflow-hidden w-full', className)}
          tabIndex={focusable ? 0 : containerProps.tabIndex}
          onPointerEnter={handlePointerEnter}
          onFocus={focusable ? handleFocus : containerProps.onFocus}
          onBlur={focusable ? handleBlur : containerProps.onBlur}
        >
          <Component ref={textRef as never} className={cn(baseTextClasses, textClassName)}>
            {children}
          </Component>
        </div>
      </TooltipTrigger>
      {isOverflown && (
        <TooltipContent
          side={tooltipSide}
          align={tooltipAlign}
          sideOffset={tooltipSideOffset}
          className={tooltipClassName}
        >
          {tooltipContent ?? children}
        </TooltipContent>
      )}
    </Tooltip>
  );
};
